import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import Pagination from "@mui/material/Pagination";
import { TbLayoutColumns } from "react-icons/tb";
import DropdownIcon from "../../assets/icons/dispatcher-dropdown.svg";
import SearchIcon from "../../assets/icons/dispatcher-search.svg";

import "./BoltTable.css";
import ColumnsModal from "../Modals/ColumnsModal/ColumnsModal";
import ActiveFilters from "./Components/ActiveFilters";
import Inbox from "@mui/icons-material/Inbox";
import CircularLoader from "../CircularLoader/CircularLoader";
import Tooltip from "../Tooltip/Tooltip";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { findSettingByType } from "../../helpers/helpers";
import TableSkeletonLoader from "../Common/TableSkeleton";
import useAutoAdjustTableHeight from "../../hooks/useAutoAdjustableHeight";
import { useTableColumns } from "../../hooks/useTableColumns";
import { useMediaQuery } from "@mui/material";
import Banner from "../Banner/Banner";
import TableHead from "./Components/TableHead";
import TableCheckbox from "./Components/TableCheckbox";
import TableFilters from "./Components/TableFilters";
import HeaderStats, { collectHeaderStats } from "./Components/HeaderStats";
import FiltersSheet from "./Components/FiltersSheet";
import { filterValueLabel } from "./Components/HeaderFilters";

const BoltTable = ({
  columns,
  data,
  getAllData,
  selectedRows,
  setSelectedRows,
  detailsComponent,
  expandable = false,
  selectable = false,
  showVariants = false,
  footer = null,
  refresh,
  message,
  searchPlaceHolder,
  handleRowClick,
  isRowClick = null,
  disableAutoResizing,
  tableId,
  tableTitle,
  alert = null,
  headerButtons = null,
  belowToolbar = null,
  maxHeight = "500px",
  // A page's own settings that change the list without being a filter here
  // (the Orders page's "Show first"): chips shown with the filters'
  // ({ key, label, value, onRemove }), and a call when Clear all is pressed.
  extraFilterChips = null,
  onClearAllFilters = null,
}) => {
  const { t } = useTranslation("common");
  const [paginationData, setpaginationData] = useState(data);
  const [editableData, setEditableData] = useState(data?.data);
  const [page, setPage] = useState(paginationData?.current_page || 1);
  const [modal, setModal] = useState(false);
  const [expandedRow, setExpandedRow] = useState();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const bottomRef = useRef(null);

  const handleFieldChange = (rowIndex, field, value) => {
    const updatedData = [...editableData];
    updatedData[rowIndex][field] = value;
    setEditableData(updatedData);
  };

  const [nestedSearch, setnestedSearch] = useState([]);
  const [nestedSearchFinal, setnestedSearchFinal] = useState([]);
  const [cols, setCols] = useState();
  const [filter, setFilter] = useState("");
  const [filterFinal, setFilterFinal] = useState("");
  const [loader, setloader] = useState("main");
  const user = useSelector((state) => state.user);
  const [popperId, setPopperId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const textFieldRef = useRef(null);
  const { tableHeight, tableRef, toolbarRef } = useAutoAdjustTableHeight(
    bottomRef,
    disableAutoResizing,
  );
  const {
    filteredCols,
    handleColChecked,
    handleShowAllCols,
    handleGroupToggle,
  } = useTableColumns(tableId, columns);
  const [adjustedColumns, setAdjustedColumns] = useState(filteredCols);
  const isSmallDevice = useMediaQuery("(max-width:600px)");
  const hasVisibleColumns = adjustedColumns?.some(
    (col) => col?.checked !== false,
  );

  useEffect(() => {
    if (!tableRef.current || !filteredCols) return;

    const recalcColumnWidths = () => {
      if (!tableRef.current) return;
      const tableWidth = tableRef.current.clientWidth;

      const visibleColumns = filteredCols.filter(
        (col) => col?.checked !== false && !col?.collapsed,
      );
      const totalColumnsWidth = visibleColumns.reduce((sum, col) => {
        return sum + (col.width ? parseInt(col.width) : 150);
      }, 0);

      if (totalColumnsWidth < tableWidth) {
        const expansionRatio = tableWidth / totalColumnsWidth;
        const newColumns = filteredCols.map((col) => {
          if (col?.checked === false || col?.collapsed) return col;
          const currentWidth = col.width ? parseInt(col.width) : 150;
          return {
            ...col,
            width: `${Math.floor(currentWidth * expansionRatio)}px`,
          };
        });
        setAdjustedColumns(newColumns);
      } else {
        setAdjustedColumns(filteredCols);
      }
    };

    recalcColumnWidths();

    // Column widths can only move when the container's width does. Height-only
    // notifications arrive on every auto-height adjustment and would otherwise
    // rebuild the column array, re-rendering every cell for nothing.
    let lastWidth = tableRef.current.clientWidth;

    const resizeObserver = new ResizeObserver(() => {
      const width = tableRef.current?.clientWidth ?? lastWidth;
      if (width === lastWidth) return;

      lastWidth = width;
      recalcColumnWidths();
    });
    resizeObserver.observe(tableRef.current);

    return () => resizeObserver.disconnect();
  }, [filteredCols]);

  const handlePopper = (event, id) => {
    const currentAnchorEl =
      anchorEl === event.currentTarget ? null : event.currentTarget;

    setPopperId(currentAnchorEl ? id : null);
    setAnchorEl(currentAnchorEl);

    if (currentAnchorEl) {
      setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 0);
    }
  };

  const handleMouseDown = (event) => {
    setIsDragging(true);
    setStartX(event.pageX - tableRef.current.offsetLeft);
    setScrollLeft(tableRef.current.scrollLeft);
  };

  const handleMouseMove = (event) => {
    if (!isDragging) return;
    const x = event.pageX - tableRef.current.offsetLeft;
    const distance = x - startX;
    tableRef.current.scrollLeft = scrollLeft - distance;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleKeyPress = (event, row, rowIndex) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleRowSelect = (id) => {
    if (id === "all") {
      const allIdsSelected = editableData.every((item) =>
        selectedRows.includes(item.id),
      );
      if (allIdsSelected) {
        setSelectedRows([]);
      } else {
        const newSelectedRows = editableData.map((item) => item.id);
        setSelectedRows(newSelectedRows);
      }
    } else {
      if (selectedRows.includes(id)) {
        const newSelectedRows = selectedRows.filter((rowId) => rowId !== id);
        setSelectedRows(newSelectedRows);
      } else {
        setSelectedRows([...selectedRows, id]);
      }
    }
  };

  const handleDropdownFilter = (fieldName, value) => {
    setFilterValues((prev) => ({ ...prev, [fieldName]: value }));
    handleNestedFilter(value, fieldName);
  };

  // Sets several filters at once ({ field_name: value }, "" clears one), for
  // a page's own controls: one update, so one request.
  const applyFilters = (changes) => {
    setFilterValues((prev) => ({ ...prev, ...changes }));
    setnestedSearch((prev) => {
      const next = [...prev];
      Object.entries(changes).forEach(([name, value]) => {
        const index = next.findIndex((item) => item.name === name);
        if (index === -1) next.push({ name, filter: value });
        else next[index] = { ...next[index], filter: value };
      });
      return next;
    });
  };

  // headerButtons and belowToolbar may be functions of the filters, so a page
  // can show and set them from its own controls (opt-in; elements render as
  // they are). belowToolbar is a full-width band under the toolbar.
  const filterApi = { values: filterValues, setFilters: applyFilters };
  const toolbarButtons =
    typeof headerButtons === "function"
      ? headerButtons(filterApi)
      : headerButtons;
  const toolbarBelow =
    typeof belowToolbar === "function"
      ? belowToolbar(filterApi)
      : belowToolbar;

  const activeFilters = useMemo(() => {
    const chips = [];

    if (filter) {
      chips.push({
        key: "__search",
        label: t("table.search"),
        value: filter,
        onRemove: () => setFilter(""),
      });
    }

    nestedSearch.forEach(({ name, filter: value }) => {
      if (!value) return;
      const column = (adjustedColumns ?? columns)
        ?.flatMap((col) => [
          col,
          ...(col?.extraFilters ?? []),
          ...(col?.headerFilters ?? []),
        ])
        .find((col) => col?.field_name === name);
      // Dropdown-style filters (Type, Carrier, ...) already show their active
      // state on the TableFilters button itself — a chip here would just
      // repeat it under a different label/casing.
      const hasOwnDropdownChip =
        Array.isArray(column?.filterOptions) && column.filterOptions.length;
      if (hasOwnDropdownChip) return;

      // A heading filter with a list shows the picked option's label
      // ("Self Pickup", not "self pickup"), or for one taking several, each
      // label or its preset's name; only headerFilters are looked at.
      const listedFilter = (adjustedColumns ?? columns)
        ?.flatMap((col) => col?.headerFilters ?? [])
        .find(
          (filter) =>
            filter?.field_name === name && Array.isArray(filter?.options),
        );

      chips.push({
        key: name,
        label: column?.label ?? name,
        value: listedFilter ? filterValueLabel(listedFilter, value) : value,
        onRemove: () => handleDropdownFilter(name, ""),
      });
    });

    (extraFilterChips ?? []).forEach((chip) => chips.push(chip));

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, nestedSearch, adjustedColumns, columns, extraFilterChips]);

  const activeDropdownCount = Object.values(filterValues).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setFilter("");
    setFilterValues({});
    // Blanking the values rather than emptying the array: the debounce effect
    // only runs while nestedSearch has entries, so [] would never refetch.
    setnestedSearch((prev) => prev.map((item) => ({ ...item, filter: "" })));
    onClearAllFilters?.();
  };

  const handlePerPage = (Perpage) => {
    setAnchorEl(null);
    setPopperId(null);
    setSelectedRows?.([]);
    fetchData(1, Perpage, "table");
  };

  const handlePage = (event, value) => {
    setPage(value);
    setAnchorEl(null);
    setPopperId(null);
    setSelectedRows?.([]);
    fetchData(value, paginationData?.per_page, "table");
  };

  const handleNestedFilter = (data, column) => {
    const foundItemIndex = nestedSearch.findIndex(
      (item) => item.name === column,
    );

    if (foundItemIndex !== -1) {
      const updatedNestedSearch = [...nestedSearch];
      updatedNestedSearch[foundItemIndex] = {
        ...updatedNestedSearch[foundItemIndex],
        filter: data,
      };
      setnestedSearch(updatedNestedSearch);
    } else {
      const newX = {
        name: column,
        filter: data,
      };
      const newNestedSearch = [...nestedSearch, newX];
      setnestedSearch(newNestedSearch);
    }
  };

  const debounceTimer = useRef(null);

  useEffect(() => {
    if (nestedSearch?.length > 0) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        setnestedSearchFinal(nestedSearch);
      }, 300);

      return () => clearTimeout(debounceTimer.current);
    }
  }, [nestedSearch]);

  const debounceTimerFilter = useRef(null);
  useEffect(() => {
    if (filter || filterFinal) {
      if (debounceTimerFilter.current) {
        clearTimeout(debounceTimerFilter.current);
      }

      debounceTimerFilter.current = setTimeout(() => {
        setFilterFinal(filter);
      }, 300);

      return () => clearTimeout(debounceTimerFilter.current);
    }
  }, [filter]);

  useLayoutEffect(() => {
    setEditableData(data?.data);
    setpaginationData(data);
    setCols(columns);
  }, [data, columns]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        anchorEl &&
        !anchorEl.contains(event.target) &&
        textFieldRef.current &&
        !textFieldRef.current.contains(event.target)
      ) {
        setPopperId(null);
        setAnchorEl(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  // One request at a time. A search, filter or page change made while a
  // request is still on its way waits for it and then runs (only the newest
  // one), so an older, slower answer can never land after a newer one and
  // put the table back to the old results.
  const getAllDataRef = useRef(getAllData);
  getAllDataRef.current = getAllData;
  const requestRunning = useRef(false);
  const requestWaiting = useRef(null);

  const fetchData = async (page, perPage, loaderType) => {
    const perPageSetting = findSettingByType(user?.general_settings, "perPage");
    const recordPerPage = perPage || perPageSetting?.value || 20;
    let nestedSearch = nestedSearchFinal
      .filter((filter) => filter.filter !== "")
      .map((filter) => `&${filter.name}=${filter.filter}`)
      .join("");
    const request = {
      page: page ?? 1,
      perPage: recordPerPage,
      filter: filterFinal,
      nestedSearch,
      loader:
        loaderType ||
        (filterFinal || nestedSearchFinal.length > 0 ? "search" : "main"),
    };
    if (requestRunning.current) {
      requestWaiting.current = request;
      setloader(request.loader);
      return;
    }
    requestRunning.current = true;
    try {
      let next = request;
      while (next) {
        requestWaiting.current = null;
        setloader(next.loader);
        try {
          await getAllDataRef.current(
            next.page,
            next.perPage,
            next.filter,
            next.nestedSearch,
          );
        } catch (error) {
          if (!requestWaiting.current) throw error;
        }
        next = requestWaiting.current;
      }
    } finally {
      requestRunning.current = false;
      setloader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterFinal, refresh, nestedSearchFinal]);

  const filtersTouched = useRef(false);

  useEffect(() => {
    if (!filtersTouched.current) {
      filtersTouched.current = true;
      return;
    }

    setSelectedRows?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFinal, nestedSearchFinal]);

  useEffect(() => {
    setPage(paginationData?.current_page || 1);
  }, [paginationData]);

  return (
    <div
      className={`rounded-lg bg-ui-surface ${
        disableAutoResizing ? "flex flex-col" : ""
      }`}
      style={{
        height: disableAutoResizing ? maxHeight : "auto",
        maxHeight: disableAutoResizing ? maxHeight : "none",
      }}
    >
          <div
            ref={toolbarRef}
            className={`py-0.5 px-1 ${
              isSmallDevice
                ? ""
                : `sm:py-[9px] sm:px-5 flex justify-between items-center${
                    toolbarBelow ? " flex-wrap" : ""
                  }`
            }`}
          >
            {isSmallDevice ? (
              <>
                <div
                  className={`flex w-full items-center gap-2 ${
                    toolbarButtons && React.Children.count(toolbarButtons) > 1
                      ? ""
                      : "justify-between"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="relative min-w-fit flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-ui-surface border border-ui-border hover:border-brand hover:text-brand transition-colors cursor-pointer text-xs font-medium text-ui-text-muted whitespace-nowrap"
                      onClick={() =>
                        setModal(modal === "columns" ? false : "columns")
                      }
                    >
                      <TbLayoutColumns size={12} className="shrink-0" />
                      <span>{t("table.columns")}</span>
                      <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-state-neutral-bg text-state-neutral-text text-[10px] font-semibold">
                        {adjustedColumns?.filter(
                          (col) => col?.checked !== false && !col?.collapsed,
                        )?.length ?? 0}
                      </span>
                      {modal === "columns" && (
                        <ColumnsModal
                          filteredCols={adjustedColumns}
                          handleColChecked={handleColChecked}
                          handleShowAllCols={handleShowAllCols}
                          onClose={() => setModal(false)}
                          style={{ top: "25px", left: 0 }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="relative flex items-center w-full gap-2 bg-ui-surface border border-ui-border rounded-lg px-3 py-1.5 transition-all duration-200 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/[0.08]">
                      <img
                        src={SearchIcon}
                        className="w-4 h-4 opacity-40"
                        alt={t("table.searchIcon")}
                      />
                      <input
                        type="text"
                        className="w-full flex-1 border-none outline-none text-xs text-ui-text bg-transparent placeholder-ui-text-subtle"
                        placeholder={searchPlaceHolder ?? t("table.searchRecord")}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      />
                      {loader === "search" && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <CircularLoader size={16} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="scrollbar-hide mt-2 flex w-full flex-nowrap items-center gap-2 overflow-x-auto [&>*]:shrink-0">
                  {/* Phones: every heading filter in one sheet (columns with
                      headerFilters), then the headings' numbers as chips. */}
                  <FiltersSheet
                    columns={adjustedColumns}
                    filterValues={filterValues}
                    onApply={handleDropdownFilter}
                  />
                  <HeaderStats
                    variant="chips"
                    stats={collectHeaderStats(adjustedColumns)}
                    filterValues={filterValues}
                    onApply={handleDropdownFilter}
                  />
                  <TableFilters
                    columns={adjustedColumns}
                    filterValues={filterValues}
                    onChange={handleDropdownFilter}
                  />
                  <ActiveFilters
                    filters={activeFilters}
                    onClearAll={handleClearAllFilters}
                    extraActiveCount={activeDropdownCount}
                    showClearAll={false}
                  />
                </div>
                {(toolbarButtons ||
                  activeFilters.length + activeDropdownCount > 1) && (
                  <div className="mt-2 flex w-full flex-wrap items-center gap-2">
                    {toolbarButtons}
                    {activeFilters.length + activeDropdownCount > 1 && (
                      <button
                        type="button"
                        onClick={handleClearAllFilters}
                        className="ml-auto shrink-0 whitespace-nowrap rounded-md border border-ui-border px-3 py-1.5 text-xs font-medium text-ui-text-muted transition-colors hover:border-state-danger-border hover:text-state-danger-text"
                      >
                        {t("actions.clearAll")}
                      </button>
                    )}
                  </div>
                )}

                {alert && (
                  <div className="mt-2 w-full">
                    <Banner
                      Content={alert}
                      className="text-xs 3xl:text-[15px] 4xl:text-lg"
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex w-full flex-wrap items-center justify-between gap-[7px]">
                  <div className="flex flex-1 flex-wrap items-center gap-[7px]">
                    {tableTitle && (
                      <div className="hidden sm:flex items-center shrink-0 ml-2 mr-1 sm:mr-2 h-full">
                        <div className="whitespace-nowrap text-lg 3xl:text-xl 4xl:text-2xl font-jakarta font-medium text-ui-text-muted dark:text-ui-text-muted tracking-tight">
                          {tableTitle}
                        </div>
                      </div>
                    )}
                    <div className="relative flex items-center w-full min-w-[220px] max-w-[260px] gap-[7px] rounded-md border border-ui-border bg-ui-surface px-[11px] py-[5px] transition-all duration-200 focus-within:border-brand focus-within:ring-[3px] focus-within:ring-brand/[0.08]">
                      <img
                        src={SearchIcon}
                        className="w-4 h-4 opacity-40 shrink-0"
                        alt={t("table.searchIcon")}
                      />
                      <input
                        type="text"
                        className="w-full flex-1 border-none outline-none text-xs 3xl:text-sm text-ui-text bg-transparent placeholder-ui-text-subtle"
                        placeholder={searchPlaceHolder ?? t("table.searchRecord")}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                      />
                      {loader === "search" && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <CircularLoader size={16} />
                        </div>
                      )}
                    </div>
                    <TableFilters
                      columns={adjustedColumns}
                      filterValues={filterValues}
                      onChange={handleDropdownFilter}
                      nowrap
                    />
                    <div className="hidden sm:block w-px h-[18px] bg-ui-border shrink-0" />
                    <div
                      className="relative flex items-center shrink-0 gap-[5px] rounded-md border border-ui-border bg-ui-surface px-[11px] py-[5px] cursor-pointer text-xs font-medium text-ui-text-muted whitespace-nowrap transition-colors hover:border-brand hover:text-brand"
                      onClick={() =>
                        setModal(modal === "columns" ? false : "columns")
                      }
                    >
                      <TbLayoutColumns size={12} className="shrink-0" />
                      <span>{t("table.columns")}</span>
                      <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-state-neutral-bg text-state-neutral-text text-[10px] font-semibold">
                        {adjustedColumns?.filter(
                          (col) => col?.checked !== false && !col?.collapsed,
                        )?.length ?? 0}
                      </span>
                      {modal === "columns" && (
                        <ColumnsModal
                          filteredCols={adjustedColumns}
                          handleColChecked={handleColChecked}
                          handleShowAllCols={handleShowAllCols}
                          onClose={() => setModal(false)}
                          style={{ top: "25px", left: 0 }}
                        />
                      )}
                    </div>
                    <ActiveFilters
                      filters={activeFilters}
                      onClearAll={handleClearAllFilters}
                      extraActiveCount={activeDropdownCount}
                    />
                    {alert && (
                      <Banner
                        Content={alert}
                        className="text-xs 3xl:text-[15px] 4xl:text-lg shrink-0"
                      />
                    )}
                  </div>
                  {toolbarButtons && (
                    <div className="flex items-center gap-[7px] flex-nowrap shrink-0">
                      {toolbarButtons}
                    </div>
                  )}
                </div>
              </>
            )}
            {/* Inside the toolbar, so the table's height follows it. The
                negative margins take back the toolbar's padding (4px at the
                sides: Bootstrap's px-1 wins over sm:px-5; 2px, then 9px from
                sm, at the bottom); in the desktop flex row the basis adds the
                sides back, as margins don't widen a flex item; min-w-0 keeps
                it that wide when its content is wider (it scrolls inside). */}
            {toolbarBelow && (
              <div className="-mx-[4px] -mb-[2px] mt-[8px] min-w-0 shrink-0 basis-[calc(100%_+_8px)] sm:-mb-[9px] sm:mt-[9px]">
                {toolbarBelow}
              </div>
            )}
          </div>
          <div
            className={`relative text-ui-text overflow-auto p-0 sm:mt-0 thin__scrollbar ${
              disableAutoResizing ? "flex-1" : ""
            }`}
            ref={tableRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{
              height: disableAutoResizing ? "100%" : `${tableHeight}px`,
              maxHeight: disableAutoResizing ? "100%" : "none",
              minHeight: "200px",
            }}
          >
            {loader === "main" || loader === "table" ? (
              <TableSkeletonLoader />
            ) : (
              <>
                <table className="min-w-fit w-full min-w-max border-collapse editable__table">
                  <TableHead
                    expandable={expandable}
                    selectable={selectable}
                    showVariants={showVariants}
                    adjustedColumns={adjustedColumns}
                    isSmallDevice={isSmallDevice}
                    selectedRows={selectedRows}
                    editableData={editableData}
                    handleRowSelect={handleRowSelect}
                    DropdownIcon={DropdownIcon}
                    popperId={popperId}
                    anchorEl={anchorEl}
                    filterValues={filterValues}
                    textFieldRef={textFieldRef}
                    handlePopper={handlePopper}
                    setPopperId={setPopperId}
                    setFilterValues={setFilterValues}
                    handleNestedFilter={handleNestedFilter}
                    handleGroupToggle={handleGroupToggle}
                  />
                  {editableData?.length > 0 ? (
                    <tbody>
                      {editableData.map((row, rowIndex) => (
                        <>
                          <Tooltip title={isRowClick ?? ""}>
                            <tr
                              key={row.id}
                              className="group border-b border-ui-border-subtle last:border-b-0"
                              onClick={
                                isRowClick ? () => handleRowClick(row) : null
                              }
                            >
                              {expandable && (
                                <td className="group-hover:bg-ui-row-hover transition-colors duration-150 overflow-hidden px-3 max-w-[120px] whitespace-nowrap text-ellipsis text-start p-[5px]">
                                  <img
                                    src={DropdownIcon}
                                    alt=""
                                    className={`w-[18px] min-w-[18px] cursor-pointer transition-all duration-500 ${
                                      expandedRow === rowIndex
                                        ? "-rotate-180"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setExpandedRow(
                                        expandedRow === rowIndex
                                          ? ""
                                          : rowIndex,
                                      )
                                    }
                                  />
                                </td>
                              )}
                              {selectable && hasVisibleColumns && (
                                <td className="group-hover:bg-ui-row-hover transition-colors duration-150 overflow-hidden max-w-[120px] px-2 whitespace-nowrap text-ellipsis text-center p-[5px]">
                                  <TableCheckbox
                                    checked={selectedRows?.includes(row?.id)}
                                    onChange={() => handleRowSelect(row?.id)}
                                  />
                                </td>
                              )}
                              {showVariants && (
                                <th
                                  className="group-hover:bg-ui-row-hover transition-colors duration-150 overflow-hidden px-2 whitespace-nowrap text-ellipsis text-center p-[5px] bg-ui-surface-sunken"
                                  style={{ fontSize: "10px", fontWeight: 400 }}
                                >
                                  {t("table.main")}
                                </th>
                              )}
                              {adjustedColumns?.map((column, index) =>
                                column?.checked !== false && !column?.collapsed ? (
                                  <td
                                    key={index}
                                    className={`group-hover:bg-ui-row-hover transition-colors duration-150 px-2 3xl:py-1.5 font-medium text-[10px] 3xl:text-[15px] 4xl:text-lg  ${
                                      !isSmallDevice && column?.fixed === "left"
                                        ? "sticky left-0 z-20 bg-ui-surface group-hover:bg-ui-row-hover"
                                        : !isSmallDevice &&
                                            column?.fixed === "right"
                                          ? "sticky right-0 z-20 bg-ui-surface group-hover:bg-ui-row-hover"
                                          : ""}`}
                                    style={{
                                      maxWidth: column.width,
                                      cursor: isRowClick ? "pointer" : "",
                                    }}
                                  >
                                    {column.render ? (
                                      <div
                                        className={`flex flex-wrap items-center gap-1 max-h-[80px] overflow-auto min-[1190px]:max-h-18 min-[1190px]:p-[0.146vw] thin__scrollbar ${
                                          column?.disabled &&
                                          column?.disabled(row)
                                            ? "opacity-30 line-through [&_span]:line-through cursor-no-drop"
                                            : ""
                                        }`}
                                      >
                                        {column.render(
                                          row,
                                          rowIndex,
                                          handleFieldChange,
                                        )}
                                      </div>
                                    ) : column.editable ? (
                                      <input
                                        type={column.field_type ?? "text"}
                                        value={row[column.field_name]}
                                        style={{ width: column.width }}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            rowIndex,
                                            column.field_name,
                                            e.target.value,
                                          )
                                        }
                                        onKeyPress={(event) => {
                                          handleKeyPress(event, row, rowIndex);
                                        }}
                                        className="w-[19px] flex items-center justify-start p-[1px] focus:outline-ui-border"
                                      />
                                    ) : (
                                      <div>{row[column.field_name]}</div>
                                    )}
                                  </td>
                                ) : null,
                              )}
                            </tr>
                          </Tooltip>

                          <tr
                            style={{ width: "100%" }}
                            className={
                              expandedRow === rowIndex
                                ? "h-fit border-b border-ui-border-subtle"
                                : ""
                            }
                          >
                            <td
                              className="h-0 px-0 min-h-0 p-0 m-0 overflow-hidden"
                              colSpan={
                                adjustedColumns?.filter(
                                  (col) => col?.checked !== false && !col?.collapsed,
                                )?.length +
                                (expandable ? 1 : 0) +
                                (selectable && hasVisibleColumns ? 1 : 0) +
                                (showVariants ? 1 : 0)
                              }
                            ></td>
                          </tr>

                          {row?.children &&
                            showVariants &&
                            row?.children?.map((varient, varientIndex) => (
                              <>
                                <tr
                                  key={varient.id}
                                  className="text-[10px] 3xl:text-[15px] 4xl:text-lg [&>td]:py-1 [&>td]:px-1 border-b border-ui-border-subtle"
                                >
                                  {expandable && (
                                    <td className="overflow-hidden px-3 max-w-[120px] whitespace-nowrap text-ellipsis text-start bg-ui-surface p-[5px]">
                                      <img
                                        src={DropdownIcon}
                                        alt=""
                                        className={`w-[18px] min-w-[18px] cursor-pointer transition-all duration-500 ${
                                          expandedRow === rowIndex
                                            ? "-rotate-180"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          setExpandedRow(
                                            expandedRow === rowIndex
                                              ? ""
                                              : rowIndex,
                                          )
                                        }
                                      />
                                    </td>
                                  )}
                                  {selectable && hasVisibleColumns && (
                                    <td className="overflow-hidden max-w-[120px] px-2 whitespace-nowrap text-ellipsis text-center p-[5px]">
                                      <TableCheckbox
                                        checked={selectedRows?.includes(
                                          parseInt(varient.parent_id),
                                        )}
                                        onChange={() =>
                                          handleRowSelect(varient?.id)
                                        }
                                        disabled
                                      />
                                    </td>
                                  )}
                                  <td className="overflow-hidden px-2 whitespace-nowrap text-ellipsis text-start p-[5px]">
                                    {varientIndex + 1}
                                  </td>
                                  {adjustedColumns?.map((column, index) =>
                                    column?.checked !== false && !column?.collapsed ? (
                                      <td
                                        key={index}
                                        className={`px-2 ${
                                          !isSmallDevice &&
                                          column?.fixed === "left"
                                            ? "sticky left-0 z-20 bg-ui-surface-sunken"
                                            : !isSmallDevice &&
                                                column?.fixed === "right"
                                              ? "sticky right-0 z-20 bg-ui-surface-sunken"
                                              : ""}`}
                                        style={{
                                          maxWidth: column.width,
                                        }}
                                      >
                                        {column.render ? (
                                          <div
                                            className={`flex flex-wrap items-center gap-1 max-h-[80px] overflow-auto min-[1190px]:max-h-18 min-[1190px]:p-[0.146vw] thin__scrollbar ${
                                              column?.disabled &&
                                              column?.disabled(row)
                                                ? "opacity-30 line-through [&_span]:line-through cursor-no-drop"
                                                : ""
                                            }`}
                                          >
                                            {column.render(
                                              varient,
                                              rowIndex,
                                              handleFieldChange,
                                            )}
                                          </div>
                                        ) : column.editable ? (
                                          <input
                                            type={column.field_type ?? "text"}
                                            value={varient[column.field_name]}
                                            style={{ width: column.width }}
                                            onChange={(e) =>
                                              handleFieldChange(
                                                varientIndex,
                                                column.field_name,
                                                e.target.value,
                                              )
                                            }
                                            onKeyPress={(event) => {
                                              handleKeyPress(
                                                event,
                                                varient,
                                                varientIndex,
                                              );
                                            }}
                                            className="w-[19px] flex items-center justify-start p-[1px] focus:outline-ui-border"
                                          />
                                        ) : (
                                          <div>
                                            {varient[column.field_name]}
                                          </div>
                                        )}
                                      </td>
                                    ) : null,
                                  )}
                                </tr>
                              </>
                            ))}
                        </>
                      ))}
                      {footer
                        ? React.cloneElement(footer, {
                            className: `${
                              footer.props.className || ""
                            } sticky bottom-0 pt-1 text-[10px] 3xl:text-[15px] 4xl:text-lg bg-ui-surface`,
                            style: { ...footer.props.style, zIndex: 10 },
                          })
                        : null}
                    </tbody>
                  ) : null}
                </table>
                {(!editableData || editableData.length === 0) && (
                  <div className="flex flex-col items-center justify-center w-full h-full min-h-[300px] text-ui-text-muted bg-ui-surface rounded-md border border-dashed border-ui-border">
                    <Inbox className="w-14 h-14 mb-2 text-ui-text-subtle" />
                    <div className="text-lg font-medium">{message ?? t("table.noData")}</div>
                  </div>
                )}
              </>
            )}
          </div>
          <div
            className={`scrollbar-hide flex min-h-[26px] flex-row flex-nowrap items-center justify-between gap-2 overflow-x-auto px-2 py-[2px] text-xs 3xl:text-[15px] 4xl:text-base text-ui-text-muted [&>*]:shrink-0 ${
              filter !== "" || editableData?.length > 0
                ? "bg-ui-surface-sunken"
                : ""
            }`}
            ref={bottomRef}
          >
            {(filter !== "" || editableData?.length > 0) && (
              <>
              <Pagination
                count={paginationData?.last_page || 1}
                onChange={handlePage}
                defaultPage={page}
                page={page}
                size="small"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                className="flex-shrink-0 sm:mb-0"
                sx={{
                  "& .MuiPagination-ul": {
                    gap: "1px",
                  },
                  "& .MuiPaginationItem-root": {
                    minWidth: "20px",
                    height: "20px",
                    fontSize: "0.7rem !important",
                    "@media (min-width: 1920px)": {
                      fontSize: "0.875rem !important",
                      minWidth: "24px",
                      height: "24px",
                    },
                    "@media (min-width: 3020px)": {
                      fontSize: "1rem !important",
                      minWidth: "28px",
                      height: "28px",
                    },
                    borderRadius: "6px",
                    border: "none",
                    color: "var(--color-ui-text-muted)",
                    backgroundColor: "transparent",
                    transition: "background 0.2s",
                    "&.Mui-selected": {
                      backgroundColor: "var(--color-brand)",
                      color: "var(--color-ui-surface)",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "var(--color-brand-hover)",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "var(--color-ui-surface-sunken)",
                    },
                  },
                  "& .MuiPaginationItem-ellipsis": {
                    fontSize: "0.7rem !important",
                    "@media (min-width: 1920px)": {
                      fontSize: "0.875rem !important",
                    },
                    "@media (min-width: 3020px)": {
                      fontSize: "1rem !important",
                    },
                    color: "var(--color-ui-text-subtle)",
                  },
                  "& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext":
                    {
                      borderRadius: "6px",
                      color: "var(--color-ui-text-muted)",
                      "& svg": {
                        fontSize: "0.9rem",
                        "@media (min-width: 1920px)": {
                          fontSize: "1rem",
                        },
                        "@media (min-width: 3020px)": {
                          fontSize: "1.125rem",
                        },
                      },
                    },
                }}
              />
              <div className="flex items-center space-x-2 sm:mb-[2px] ml-auto text-xs 3xl:text-[15px] 4xl:text-base">
                <span className="hidden sm:inline">{t("table.rowsPerPage")}</span>
                <span className="sm:hidden">{t("table.rows")}</span>
                <select
                  onChange={(e) => handlePerPage(e.target.value)}
                  value={paginationData?.per_page || 10}
                  className="border border-ui-border rounded-md px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-brand bg-ui-surface text-xs 3xl:text-[15px] 4xl:text-base h-5 3xl:h-6 4xl:h-7 max-h-5 3xl:max-h-6 4xl:max-h-7"
                  style={{
                    height: "20px",
                    maxHeight: "20px",
                    fontSize: "0.7rem",
                  }}
                >
                  {[10, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200].map(
                    (value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
                <span>{t("table.total")} {paginationData?.total || 0}</span>
              </div>
              </>
            )}
          </div>
    </div>
  );
};

export default BoltTable;
